import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { RequestAccessDialog } from '../components/RequestAccessDialog';
import { createSharedContext } from './sharedContext';
import { useNotification } from './NotificationContext';
import {
  clearRequestAccessHandler,
  registerRequestAccessHandler,
} from '../utils/permissionErrors';

const PermissionRequestContext = createSharedContext('PermissionRequest');

/**
 * Hook to open the request-access dialog directly.
 * @returns {{ requestAccess: (roles?: string[]) => void }}
 */
export const usePermissionRequest = () => {
  const context = useContext(PermissionRequestContext);
  if (!context) {
    throw new Error('usePermissionRequest must be used within a PermissionRequestProvider');
  }
  return context;
};

/**
 * PermissionRequestProvider - Lets a denied user request the missing role in place.
 *
 * Mount this once per app, inside `NotificationProvider` (it uses
 * `useNotification` to report the outcome). It owns `RequestAccessDialog` and
 * registers itself as the handler behind the "Request access" button that
 * `notifyPermissionError` puts on permission-denied toasts — so existing catch
 * blocks need no changes at all. Without this provider those buttons stay
 * deep links into the Permissions app.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {Object} props.api - App transport from `createAppTransport`
 * @param {string} props.appName - App slug, e.g. 'recon' (used in the default reason)
 *
 * @example
 * // In App.js
 * <NotificationProvider>
 *   <PermissionRequestProvider api={api} appName="recon">
 *     <Dashboard />
 *   </PermissionRequestProvider>
 * </NotificationProvider>
 */
export const PermissionRequestProvider = ({ children, api, appName }) => {
  const { showNotification } = useNotification();
  const [state, setState] = useState({ open: false, roles: [] });

  const requestAccess = useCallback((roles = []) => {
    setState({ open: true, roles: Array.isArray(roles) ? roles : [] });
  }, []);

  const close = useCallback(() => setState((prev) => ({ ...prev, open: false })), []);

  // Registering here (rather than passing a callback through every
  // notifyPermissionError call site) is what makes adoption a one-line
  // change per app. Cleared on unmount so a torn-down provider can't be
  // called and tests don't leak a handler into the next case.
  useEffect(() => {
    registerRequestAccessHandler(requestAccess);
    return clearRequestAccessHandler;
  }, [requestAccess]);

  const handleSubmitted = useCallback(
    ({ roleId, alreadyPending, alreadyGranted }) => {
      // The server short-circuits both of these without writing a row —
      // already_granted in particular, because a PENDING row on top of an
      // ACTIVE grant would revoke the access the user already has.
      if (alreadyGranted) {
        showNotification(
          `You already have the "${roleId}" role. If the action still fails, ` +
            'try reloading the page.',
          'info'
        );
        return;
      }
      showNotification(
        alreadyPending
          ? `You already have a pending request for "${roleId}".`
          : `Access requested — an administrator will review your request for "${roleId}".`,
        alreadyPending ? 'info' : 'success'
      );
    },
    [showNotification]
  );

  // Memoized: the provider re-renders on every dialog state change, and a
  // fresh object each time would re-run consumer effects that depend on the
  // context — looping outright if such an effect calls requestAccess.
  const value = useMemo(() => ({ requestAccess }), [requestAccess]);

  return (
    <PermissionRequestContext.Provider value={value}>
      {children}
      <RequestAccessDialog
        open={state.open}
        onClose={close}
        api={api}
        appName={appName}
        roles={state.roles}
        onSubmitted={handleSubmitted}
      />
    </PermissionRequestContext.Provider>
  );
};

export default PermissionRequestContext;
